import { z } from "zod/v4";

export const courtAvailProviderSchema = z.enum([
	"mycourt_berlin",
	"manual",
	"eversports",
	"funkhaus",
	"ebusy",
]);

export const courtAvailEnvironmentSchema = z.enum(["outdoor", "indoor"]);

// Server-derived source status. The API owns the thresholds (a manual source is
// never "stale" — nothing scrapes it), so the dashboard only renders them.
export const courtAvailHealthSchema = z.enum(["paused", "error", "stale", "empty", "healthy"]);

// Body to register an operator-fed "manual" source for a venue.
export const createCourtAvailSourceSchema = z.object({
	location_id: z.string().uuid(),
	label: z.string().min(1),
	environment: courtAvailEnvironmentSchema,
	durations: z.array(z.number().int().positive()),
	weeks_ahead: z.number().int().positive(),
	booking_url: z.string().url().nullable().optional(),
});

// Full replace of a source's editable config, including the venue it feeds —
// seeded scraper sources start unlinked and get attached here.
export const updateCourtAvailSourceSchema = z.object({
	location_id: z.string().uuid().nullable(),
	label: z.string().min(1),
	environment: courtAvailEnvironmentSchema,
	durations: z.array(z.number().int().positive()).min(1),
	weeks_ahead: z.number().int().positive(),
	booking_url: z.string().url().nullable(),
	active: z.boolean(),
});

// One operator-entered open slot (starts_at is an ISO instant).
export const courtAvailSlotInputSchema = z.object({
	starts_at: z.string(),
	duration_min: z.number().int().positive(),
	free_courts: z.number().int().min(0),
	price_cents: z.number().int().nullable().optional(),
	heated: z.boolean().optional(),
});

export const createCourtAvailSourceResultSchema = z.object({
	id: z.string(),
	label: z.string(),
});

export const updateCourtAvailSourceResultSchema = z.object({
	id: z.string(),
	label: z.string(),
	active: z.boolean(),
});

export const ingestCourtSlotsResultSchema = z.object({
	saved: z.number().int(),
	alerted: z.boolean(),
});

// One configured scraper source plus live aggregates over the slots it holds.
// Backs the admin court-availability insights dashboard.
export const courtAvailSourceInsightSchema = z.object({
	id: z.string(),
	location_id: z.string().nullable(),
	location_name: z.string().nullable(),
	location_city: z.string().nullable(),
	provider: courtAvailProviderSchema,
	external_code: z.string(),
	label: z.string(),
	environment: courtAvailEnvironmentSchema,
	durations: z.array(z.number()),
	weeks_ahead: z.number().int(),
	active: z.boolean(),
	booking_url: z.string().nullable(),
	health: courtAvailHealthSchema,
	last_synced_at: z.coerce.date().nullable(),
	last_error: z.string().nullable(),
	upcoming_slots: z.number().int(),
	days_covered: z.number().int(),
	total_free_courts: z.number().int(),
	next_24h_slots: z.number().int(),
	next_24h_free_courts: z.number().int(),
	next_start: z.coerce.date().nullable(),
	last_start: z.coerce.date().nullable(),
	last_scraped_at: z.coerce.date().nullable(),
});

// One upcoming slot a source currently holds, as the admin inspector reads it.
export const courtAvailSlotSchema = z.object({
	starts_at: z.coerce.date(),
	time: z.string(),
	duration_min: z.number().int(),
	free_courts: z.number().int(),
	price_cents: z.number().int().nullish(),
	heated: z.boolean(),
	environment: courtAvailEnvironmentSchema,
	source_label: z.string(),
	booking_url: z.string().nullish(),
});

export const courtAvailDaySchema = z.object({
	date: z.string(),
	slots: z.array(courtAvailSlotSchema),
});

// Everything one source holds right now, grouped into venue-local days.
export const courtAvailSourceSlotsSchema = z.object({
	source_id: z.string(),
	label: z.string(),
	timezone: z.string(),
	days: z.array(courtAvailDaySchema),
});

// Result of scraping a single source on demand. failed carries the recorded
// error text, so the operator sees why a run produced nothing.
export const courtAvailSyncOutcomeSchema = z.object({
	source_id: z.string(),
	saved: z.number().int(),
	complete: z.boolean(),
	failed: z.string().nullable(),
});

export const courtAvailSyncResultSchema = z.object({
	status: z.string(),
});

export type CourtAvailProvider = z.infer<typeof courtAvailProviderSchema>;
export type CourtAvailEnvironment = z.infer<typeof courtAvailEnvironmentSchema>;
export type CourtAvailHealth = z.infer<typeof courtAvailHealthSchema>;
export type CourtAvailSourceInsight = z.infer<typeof courtAvailSourceInsightSchema>;
export type CreateCourtAvailSource = z.infer<typeof createCourtAvailSourceSchema>;
export type UpdateCourtAvailSource = z.infer<typeof updateCourtAvailSourceSchema>;
export type CourtAvailSlotInput = z.infer<typeof courtAvailSlotInputSchema>;
export type CourtAvailSlot = z.infer<typeof courtAvailSlotSchema>;
export type CourtAvailDay = z.infer<typeof courtAvailDaySchema>;
export type CourtAvailSourceSlots = z.infer<typeof courtAvailSourceSlotsSchema>;
export type CourtAvailSyncOutcome = z.infer<typeof courtAvailSyncOutcomeSchema>;
